<template>
  <div class="lab-form-content">
    <div class="flex flex-wrap justify-end">
      <vs-input
        class="w-full name-field"
        label="Lab name"
        v-model="localLab.name"
        @blur="saveLab(localLab)"
      />
    </div>
    <div class="w-full mt-10">
      <label class="vs-input--label">Slug</label>
      <vx-input-group
        class="mb-base"
        prependClasses="border border-solid border-grey border-r-0"
      >
        <template slot="prepend">
          <div class="prepend-text">
            <vs-switch
              v-model="autoAlias"
              :checked="autoAlias"
              class="auto-alias-checkbox"
            />
          </div>
        </template>

        <vs-input
          v-model="localLab.slug"
          :disabled="autoAlias"
          class="auto-alias-field"
          @blur="saveLab(localLab)"
        />
      </vx-input-group>
    </div>
    <div class="w-full mt-10">
      <label class="vs-input--label">Link Manual id:</label>
      <vx-input-group
        class="mb-base"
        prependClasses="border border-solid border-grey border-r-0"
      >
        <template slot="prepend">
          <div class="prepend-text">
            <vs-switch
              v-model="linkManual"
              :checked="linkManual"
              class="auto-alias-checkbox"
            />
          </div>
        </template>

        <vs-input
          v-model="localLab.metadata.pta_manual_id"
          placeholder="Enter lab manual id from ARGUS"
          :disabled="!linkManual"
          class="auto-alias-field"
          @blur="saveLab(localLab)"
        />

        <template slot="append" v-if="linkManual">
          <div class="append-btn-wrapper ml-4">
            <vs-button class="append-btn" @click="fetchManualId">
              Fetch
            </vs-button>
          </div>
        </template>
      </vx-input-group>
    </div>
    <div class="w-full mt-10 pb-10" v-if="embedded">
      <div class="w-full content-status">
        <label class="vs-input--label">Status</label>
        <v-select
          v-model="localLab.status"
          :clearable="false"
          placeholder="Select one"
          :options="getLabAllStatus"
          :reduce="(status) => status.value"
          @input="saveLab(localLab)"
        />
      </div>
    </div>
    <div class="w-full flex flex-wrap">
      <div class="flex w-1/3 mt-10">
        <vs-switch
          @change="saveLab(localLab)"
          v-model="localLab.is_lab_experience"
          class="mr-3 is-lab-experience-field"
        />
        <label>Is Skill Dive Lab</label>
      </div>
      <div class="flex w-1/3 mt-10">
        <vs-switch
          @change="saveLab(localLab)"
          v-model="localLab.is_featured"
          class="mr-3 is-featured-field"
        />
        <label>Is Featured</label>
      </div>
      <div class="flex w-1/3 mt-10">
        <vs-switch
          @change="saveLab(localLab)"
          v-model="localLab.is_skill_check"
          class="mr-3 is-featured-field"
        />
        <label>Is Skill Check</label>
      </div>
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <Skills
        :content-type="CONTENT_TYPES.LAB"
        :skills-list="labSkills"
        :editMode="isEditing"
        @add:skillsList="handleLabSkillsAdded"
        @delete:skillsList="handleLabSkillsDeleted"
      />
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <vs-input
        class="w-full duration-field"
        label="Duration"
        v-model="duration"
        readonly
      />
    </div>
    <div
      id="lab-async-api-data-2"
      class="flex mt-10 flex-wrap vs-con-loading__container"
    >
      <div class="w-1/2 pb-5 pr-3">
        <label class="vs-input--label">Learning Areas</label>
        <v-select
          multiple
          v-model="localLab.learning_areas"
          placeholder="Select one or more areas"
          :options="getLAreas"
          :reduce="(la) => la.value"
          @input="saveLab(localLab)"
        />
      </div>
      <div class="w-1/2 pb-5">
        <label class="vs-input--label">Main Learning Area</label>
        <v-select
          v-model="localLab.main_learning_area"
          placeholder="Select main area"
          :options="mainLAreaOptions"
          :reduce="(la) => la.value"
          :resetOnOptionsChange="validateMainLArea"
        />
      </div>
      <div class="w-1/2 pb-5 pr-3">
        <label class="vs-input--label">Categories</label>
        <v-select
          multiple
          v-model="localLab.categories"
          placeholder="Select one or more categories"
          :reduce="(category) => category.value"
          :options="getLabsCategories"
          @input="saveLab(localLab)"
        />
      </div>
      <div class="w-1/2 pb-5">
        <label class="vs-input--label">Difficulty</label>
        <v-select
          v-model="localLab.difficulty"
          placeholder="Select a level"
          :reduce="(difficulty) => difficulty.value"
          :options="getLabDifficulties"
          @input="saveLab(localLab)"
        />
      </div>
      <div class="w-full pb-5">
        <label class="vs-input--label">Tags</label>
        <v-select
          v-model="localLab.tags"
          multiple
          taggable
          push-tags
          :closeOnSelect="false"
          placeholder="Select one or more tags"
          :options="tagsOptions"
          :filterable="false"
          @search="onSearchTags"
          @input="saveLab(localLab)"
        >
          <template slot="no-options">type to search or add a tag</template>
        </v-select>
      </div>
      <div class="w-1/2 pr-3 pb-5">
        <label class="vs-input--label">Type</label>
        <v-select
          v-model="localLab.lab_type"
          :options="labTypeOptions"
          placeholder="Select a type of lab"
          :clearable="false"
          :reduce="(type) => type.value"
          @input="saveLab(localLab)"
        />
      </div>
      <div class="w-1/2 pb-5">
        <label class="vs-input--label">Severity</label>
        <v-select
          class="w-full severity-field"
          v-model="localLab.severity"
          :options="labSeverityOptions"
          placeholder="Select the severity level"
          :reduce="(severity) => severity.value"
          @input="saveLab(localLab)"
        />
      </div>
      <div class="w-1/2 pr-3 pb-5">
        <label class="vs-input--label">Year</label>
        <v-select
          :options="labYearOptions"
          @input="saveLab(localLab)"
          v-model="localLab.year"
          class="w-full year-field"
          placeholder="Select year"
        />
      </div>
      <div class="w-1/2 pb-5">
        <label class="vs-input--label">CVE</label>
        <vs-input
          @blur="saveLab(localLab)"
          v-mask="'CVE-####-#####'"
          v-model="localLab.cve"
          placeholder="CVE-YYYY-#####"
          class="w-full cve-field"
        />
      </div>
      <div class="w-full pb-5">
        <label class="vs-input--label">Classification</label>
        <v-select
          :options="classificationOptions"
          @input="saveLab(localLab)"
          v-model="localLab.classification"
          class="w-full classification-field"
          placeholder="Select classification"
        />
      </div>
    </div>
    <div
      class="flex flex-wrap pb-10 justify-end"
      v-if="localLab.lab_type === 'els'"
    >
      <vs-input
        class="w-full url-field"
        label="ELS Lab Id"
        type="number"
        v-model.number="localLab.metadata.els_lab_id"
        @blur="saveLab(localLab)"
      />
    </div>
    <div
      class="flex flex-wrap pb-10 justify-end"
      v-if="localLab.lab_type === 'pta'"
    >
      <vs-input
        class="w-full sdn-field"
        label="PTA SDN"
        v-model="localLab.metadata.pta_sdn"
        @blur="saveLab(localLab)"
      />
    </div>
    <div
      class="flex flex-wrap pb-10 justify-end"
      v-if="localLab.lab_type === 'pta'"
    >
      <vs-input
        class="w-full namespace-field"
        label="PTA Namespace"
        v-model.number="localLab.metadata.pta_namespace"
        @blur="saveLab(localLab)"
      />
    </div>
    <div
      class="flex flex-wrap pb-10 justify-end"
      v-if="localLab.lab_type !== 'els'"
    >
      <vs-input
        class="w-full url-field"
        label="External URL"
        v-model="localLab.external_url"
        @blur="saveLab(localLab)"
      />
    </div>
    <div class="w-full pb-10">
      <label class="vs-input--label">Access Type</label>
      <v-select
        v-model="localLab.access_type"
        placeholder="Select an access type"
        :options="labAccessTypeOptions"
        @input="saveLab(localLab)"
        :clearable="false"
      />
    </div>
    <div class="flex w-full flex-wrap pb-10">
      <label class="vs-input--label">Explanation video</label>
      <v-select
        class="w-full"
        v-model="localLab.explanation_video"
        placeholder="Select a video"
        :options="explanationOptions"
        :filterable="false"
        @search="onSearchExplanationVideos"
        :reduce="(video) => video.value"
        @input="saveLab(localLab)"
      >
        <template slot="no-options">type to search a video</template>
      </v-select>
    </div>
    <div class="flex w-full flex-wrap pb-10">
      <label class="vs-input--label">Solution video</label>
      <v-select
        class="w-full"
        v-model="localLab.solution_video"
        placeholder="Select a video"
        :options="solutionOptions"
        :filterable="false"
        @search="onSearchSolutionVideos"
        :reduce="(video) => video.value"
        @input="saveLab(localLab)"
      >
        <template slot="no-options">type to search a video</template>
      </v-select>
    </div>
    <div class="edit-description-group w-full flex flex-wrap justify-end mb-10">
      <label class="w-full" for="">Overview</label>
      <markdown-editor
        :id="localLab.id"
        class="w-full description-field"
        ref="markdownEditor"
        v-model="localLab.description"
        :options="{
          hideModeSwitch: true,
          previewStyle: 'tab',
        }"
        @input="saveLab(localLab)"
      />
    </div>
    <div class="edit-tasks-group w-full flex flex-wrap justify-end mb-10">
      <label class="w-full" for="">Tasks</label>
      <markdown-editor
        :id="localLab.id"
        class="w-full tasks-field"
        ref="markdownEditor"
        v-model="localLab.tasks"
        :options="{
          hideModeSwitch: true,
          previewStyle: 'tab',
        }"
        @input="saveLab(localLab)"
      />
    </div>
    <div class="edit-solutions-group w-full flex flex-wrap justify-end mb-10">
      <label class="w-full" for="">Solutions</label>
      <markdown-editor
        :id="localLab.id"
        class="w-full solutions-field"
        ref="markdownEditor"
        v-model="localLab.solutions"
        :options="{
          hideModeSwitch: true,
          previewStyle: 'tab',
        }"
        @input="saveLab(localLab)"
      />
    </div>

    <div class="flex md:mt-10 mt-6 -mx-2 action-buttons" v-if="embedded">
      <vs-button
        class="w-full mx-2 save-button"
        color="primary"
        icon-pack="feather"
        @click.stop="saveAndClick"
      >
        Save
      </vs-button>
    </div>
  </div>
</template>
<script>
import vSelect from 'vue-select'
import slug from 'slug-generator'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import MarkdownEditor from '@/components/markdownEditor'
import { searchVideos } from '@/utils/mixins'
import Skills from '@/components/Skills'
import CONSTANTS from '@/constants'
import { HmsToSeconds, secondsToHms } from '@/utils/utils.js'
import { searchLabsTags, getBasicData } from '@/utils/mixins'
import { mask } from 'vue-the-mask'

export default {
  mixins: [searchVideos, searchLabsTags, getBasicData],
  props: {
    embedded: Boolean,
    onClick: {
      type: Function,
      required: true,
    },
  },
  directives: {
    mask,
  },
  components: {
    MarkdownEditor,
    'v-select': vSelect,
    Skills,
  },
  data() {
    return {
      CONTENT_TYPES: CONSTANTS.CONTENT_TYPES,
      autoAlias: true,
      linkManual: false,
      localLab: {},
      labAccessTypeOptions: ['web', 'default'],
      explanationOptions: [],
      solutionOptions: [],
      duration: '00:00:00',
      durationInSeconds: '0',
      classificationOptions: [
        'cognitive',
        'governance',
        'healthcare',
        'economic',
        'communication',
        'technical',
      ],
    }
  },
  watch: {
    'localLab.name': function (newValue) {
      if (this.autoAlias) {
        this.localLab.slug = slug(newValue)
      }
    },
    autoAlias: function (newValue) {
      if (newValue) {
        this.localLab.slug = slug(this.localLab.name)
      }
    },
    getCurrentLab: function (newValue) {
      this.localLab = cloneDeep(newValue)
      this.durationInSeconds = this.localLab.duration_in_seconds
      this.duration = secondsToHms(this.durationInSeconds)
      this.getVideoEntries()
    },
    duration: function (newValue) {
      this.durationInSeconds = HmsToSeconds(newValue)
      this.transformAndSaveLab()
    },
  },
  computed: {
    ...mapGetters('labs', [
      'getCurrentLab',
      'getLabAllStatus',
      'getLabDifficulties',
      'getLabsCategories',
      'labSeverityOptions',
      'labTypeOptions',
      'labYearOptions',
    ]),
    ...mapGetters('taxonomy', ['getLabTaxonomy']),
    ...mapGetters('lareas', ['getLAreas']),
    isEditing() {
      return !!this.getCurrentLab.id
    },
    mainLAreaOptions() {
      try {
        if (this.getLAreas && this.localLab.learning_areas) {
          return this.getLAreas.filter((la) =>
            this.localLab.learning_areas.includes(la.value)
          )
        } else return []
      } catch (error) {
        return []
      }
    },
    labSkills() {
      const currentSkills = Array.isArray(this.localLab.taxonomy_skills)
        ? this.localLab.taxonomy_skills
        : []
      const addedSkills = this.getLabTaxonomy && this.getLabTaxonomy.contentId === this.localLab.id && Array.isArray(this.getLabTaxonomy.addedSkills) ? this.getLabTaxonomy.addedSkills : []
      return [...currentSkills, ...addedSkills]
    },
  },
  methods: {
    ...mapMutations('labs', {
      saveLab: 'STORE_CURRENT_LAB',
    }),
    ...mapMutations('taxonomy', {
      storeLabTaxonomy: 'STORE_LAB_TAXONOMY',
      deleteLabTaxonomy: 'DELETE_LAB_TAXONOMY_SKILL',
      clearLabTaxonomy: 'CLEAR_LAB_TAXONOMY',
    }),
    ...mapActions('videos', ['searchVideos']),
    saveAndClick() {
      this.saveLab(this.localLab)
      this.onClick()
    },
    saveLabIfNew() {
      if (!this.isEditing) {
        this.saveLab(this.localLab)
      }
    },
    saveLocalLab() {
      this.saveLab(this.localLab)
    },
    onSearchExplanationVideos(search, loading) {
      this.onSearchVideos(
        search,
        loading,
        'explanationOptions',
        'type=lab_explanation'
      )
    },
    onSearchSolutionVideos(search, loading) {
      this.onSearchVideos(
        search,
        loading,
        'solutionOptions',
        'type=lab_solution'
      )
    },
    async getVideoEntries() {
      if (
        (this.localLab.explanation_video && !this.explanationOptions.length) ||
        (this.localLab.solution_video && !this.solutionOptions.length)
      ) {
        // we need to get the videos to show their names
        const ids = []
        if (this.localLab.explanation_video)
          ids.push(this.localLab.explanation_video)
        if (this.localLab.solution_video) ids.push(this.localLab.solution_video)
        const response = await this.searchVideos({
          ids: ids,
        })
        this.explanationOptions = response.results.reduce((acc, v) => {
          if (v.type === 'lab_explanation') {
            acc.push({ label: v.name, value: v.id })
          }
          return acc
        }, [])
        this.solutionOptions = response.results.reduce((acc, v) => {
          if (v.type === 'lab_solution') {
            acc.push({ label: v.name, value: v.id })
          }
          return acc
        }, [])
      }
    },
    transformAndSaveLab() {
      this.localLab.duration_in_seconds = this.durationInSeconds
      this.saveLab(this.localLab)
    },
    validateMainLArea() {
      return !(
        this.localLab.learning_areas &&
        this.localLab.learning_areas.includes(this.localLab.main_learning_area)
      )
    },
    ...mapActions('labs', ['getLabByManualId']),
    async fetchManualId() {
      try {
        this.$vs.loading()
        await this.getLabByManualId(this.localLab.metadata.pta_manual_id)
      } catch (error) {
        this.showError(error.response.data)
      }
      this.$vs.loading.close()
    },
    handleLabSkillsAdded(newSkillsList) {
      const skillsToAdd = newSkillsList.filter(
        (s) => !this.labSkills.find(existingSkill => existingSkill.skillId === s.skillId)
      )
      this.storeLabTaxonomy({
        contentId: this.getCurrentLab.id,
        addedSkills: skillsToAdd,
      })

    },
    handleLabSkillsDeleted(deletedSkill) {
      this.deleteLabTaxonomy({
        contentId: this.getCurrentLab.id,
        skillId: deletedSkill.skillId,
      })
    },
  },
  async created() {
    this.localLab = cloneDeep(this.getCurrentLab)
    this.durationInSeconds = this.localLab.duration_in_seconds
    this.duration = secondsToHms(this.durationInSeconds)
    this.getVideoEntries()
    this.clearLabTaxonomy({contentId: this.localLab.id})
  },
  async mounted() {
    try {
      this.$vs.loading({
        container: '#lab-async-api-data-2',
        scale: 0.6,
      })

      // @mixin
      await this.getBasicLabData()
    } catch (error) {
      this.$vs.loading.close('#lab-async-api-data-2 > .con-vs-loading')
      this.showError(error)
    }
    this.$vs.loading.close('#lab-async-api-data-2 > .con-vs-loading')
  },
}
</script>
<style lang="scss">
.vs-con-loading__container {
  position: relative;
  overflow: unset;
}
</style>
